import React, {useEffect, useRef} from "react";
import "./SpaceBackground.css";

/* ── Inline SVG Rocket ──────────────────────────────────── */
function Rocket({className, style}) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 48 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Body */}
      <ellipse cx="24" cy="38" rx="13" ry="24" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2"/>
      {/* Nose cone */}
      <path d="M24 6 L37 30 Q24 23 11 30 Z" fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.45)" strokeWidth="1"/>
      {/* Window */}
      <circle cx="24" cy="36" r="6" fill="none" stroke="rgba(167,139,250,0.85)" strokeWidth="1.2"/>
      <circle cx="24" cy="36" r="3.2" fill="rgba(124,58,237,0.7)"/>
      {/* Left fin */}
      <path d="M11 54 L3 70 L15 62 Z" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.38)" strokeWidth="1"/>
      {/* Right fin */}
      <path d="M37 54 L45 70 L33 62 Z" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.38)" strokeWidth="1"/>
      {/* Flame outer */}
      <ellipse cx="24" cy="70" rx="6" ry="9" fill="rgba(124,58,237,0.6)"/>
      {/* Flame inner */}
      <ellipse cx="24" cy="73" rx="3.5" ry="5.5" fill="rgba(167,139,250,0.75)"/>
      {/* Flame core */}
      <ellipse cx="24" cy="75" rx="1.8" ry="3" fill="rgba(220,210,255,0.95)"/>
    </svg>
  );
}

/* ── Inline SVG Satellite ───────────────────────────────── */
function Satellite({className, style}) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 80 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Left solar panel */}
      <rect x="2" y="12" width="24" height="16" rx="2" fill="rgba(124,58,237,0.4)" stroke="rgba(124,58,237,0.75)" strokeWidth="1"/>
      <line x1="2" y1="20" x2="26" y2="20" stroke="rgba(167,139,250,0.6)" strokeWidth="0.8"/>
      <line x1="10" y1="12" x2="10" y2="28" stroke="rgba(167,139,250,0.6)" strokeWidth="0.8"/>
      <line x1="18" y1="12" x2="18" y2="28" stroke="rgba(167,139,250,0.6)" strokeWidth="0.8"/>
      {/* Arm left */}
      <rect x="26" y="18.5" width="8" height="3" rx="1" fill="rgba(255,255,255,0.4)"/>
      {/* Body */}
      <rect x="34" y="12" width="12" height="16" rx="3" fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.5)" strokeWidth="1"/>
      {/* Dish */}
      <ellipse cx="40" cy="10" rx="5" ry="3" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.5)" strokeWidth="1"/>
      <line x1="40" y1="10" x2="40" y2="14" stroke="rgba(255,255,255,0.4)" strokeWidth="0.8"/>
      {/* Arm right */}
      <rect x="46" y="18.5" width="8" height="3" rx="1" fill="rgba(255,255,255,0.4)"/>
      {/* Right solar panel */}
      <rect x="54" y="12" width="24" height="16" rx="2" fill="rgba(124,58,237,0.4)" stroke="rgba(124,58,237,0.75)" strokeWidth="1"/>
      <line x1="54" y1="20" x2="78" y2="20" stroke="rgba(167,139,250,0.6)" strokeWidth="0.8"/>
      <line x1="62" y1="12" x2="62" y2="28" stroke="rgba(167,139,250,0.6)" strokeWidth="0.8"/>
      <line x1="70" y1="12" x2="70" y2="28" stroke="rgba(167,139,250,0.6)" strokeWidth="0.8"/>
    </svg>
  );
}

/* ── Planet SVG ─────────────────────────────────────────── */
function Planet({className, style, color1, color2, hasRing}) {
  const c1 = color1 || "rgba(124,58,237,0.7)";
  const c2 = color2 || "rgba(167,139,250,0.5)";
  return (
    <svg className={className} style={style} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id={`pg-${className}`} cx="38%" cy="35%" r="62%">
          <stop offset="0%" stopColor={color1 || "rgba(167,139,250,0.4)"}/>
          <stop offset="100%" stopColor={color2 || "rgba(30,10,60,0.3)"}/>
        </radialGradient>
      </defs>
      {hasRing && (
        <ellipse cx="50" cy="54" rx="46" ry="10" fill="none" stroke={c1} strokeWidth="2.5" opacity="0.5"/>
      )}
      <circle cx="50" cy="50" r="34" fill={`url(#pg-${className})`} stroke={c1} strokeWidth="1"/>
      {/* Surface detail lines */}
      <ellipse cx="50" cy="44" rx="28" ry="7" fill="none" stroke={c2} strokeWidth="1" opacity="0.5"/>
      <ellipse cx="50" cy="56" rx="22" ry="5" fill="none" stroke={c2} strokeWidth="0.8" opacity="0.4"/>
      {hasRing && (
        <ellipse cx="50" cy="46" rx="46" ry="10" fill="none" stroke={c1} strokeWidth="2.5" opacity="0.25"/>
      )}
    </svg>
  );
}

/* ── Asteroid / Rock ────────────────────────────────────── */
function Asteroid({className, style}) {
  return (
    <svg className={className} style={style} viewBox="0 0 40 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 22 L5 14 L10 7 L18 4 L28 6 L35 12 L34 20 L26 26 L14 27 Z"
        fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.35)" strokeWidth="1"/>
      <path d="M12 10 L16 8 L14 14 Z" fill="rgba(255,255,255,0.15)"/>
      <path d="M22 18 L27 15 L25 22 Z" fill="rgba(255,255,255,0.15)"/>
      <circle cx="19" cy="12" r="1.5" fill="rgba(255,255,255,0.3)"/>
    </svg>
  );
}

/* ── Star canvas (twinkling) ────────────────────────────── */
export default function SpaceBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = document.documentElement.scrollHeight || window.innerHeight;
    };
    resize();
    // Re-measure after layout settles
    setTimeout(resize, 300);

    class Star {
      constructor() {
        this.reset();
        this.twinkleOffset = Math.random() * Math.PI * 2;
        this.twinkleSpeed = 0.02 + Math.random() * 0.03;
      }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.baseSize = Math.random() * 2.8 + 0.8;
        this.baseOpacity = Math.random() * 0.65 + 0.25;
        this.speedX = (Math.random() - 0.5) * 0.15;
        this.speedY = (Math.random() - 0.5) * 0.15;
      }
      update(t) {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
        this.opacity = this.baseOpacity * (0.6 + 0.4 * Math.sin(t * this.twinkleSpeed + this.twinkleOffset));
        this.size = this.baseSize * (0.85 + 0.15 * Math.sin(t * this.twinkleSpeed + this.twinkleOffset));
      }
      draw() {
        ctx.fillStyle = `rgba(255,255,255,${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const stars = Array.from({length: 300}, () => new Star());
    let t = 0;
    let animId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      t++;
      stars.forEach(s => { s.update(t); s.draw(); });
      animId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      resize();
      stars.forEach(s => s.reset());
    };
    window.addEventListener("resize", handleResize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", handleResize); };
  }, []);

  return (
    <div className="space-bg">
      {/* Twinkling star canvas */}
      <canvas ref={canvasRef} className="space-canvas" />

      {/* Nebula / ambient glows — 4 spread across full page */}
      <div className="nebula nebula-1" />
      <div className="nebula nebula-2" />
      <div className="nebula nebula-3" />
      <div className="nebula nebula-4" />

      {/* Planets — distributed from top to bottom of page */}
      <Planet className="space-planet planet-1" color1="rgba(124,58,237,0.7)" color2="rgba(50,20,100,0.85)" hasRing={true} />
      <Planet className="space-planet planet-2" color1="rgba(0,200,255,0.55)" color2="rgba(0,60,120,0.7)" hasRing={false} />
      <Planet className="space-planet planet-3" color1="rgba(200,100,60,0.45)" color2="rgba(80,30,10,0.65)" hasRing={false} />
      <Planet className="space-planet planet-4" color1="rgba(100,200,150,0.4)" color2="rgba(20,70,50,0.6)" hasRing={false} />

      {/* Rockets — scattered throughout */}
      <Rocket className="space-rocket rocket-1" />
      <Rocket className="space-rocket rocket-2" />
      <Rocket className="space-rocket rocket-3" />
      <Rocket className="space-rocket rocket-4" />

      {/* Satellites — visible across multiple sections */}
      <Satellite className="space-satellite satellite-1" />
      <Satellite className="space-satellite satellite-2" />
      <Satellite className="space-satellite satellite-3" />

      {/* Asteroids — tumbling throughout the page */}
      <Asteroid className="space-asteroid asteroid-1" />
      <Asteroid className="space-asteroid asteroid-2" />
      <Asteroid className="space-asteroid asteroid-3" />
      <Asteroid className="space-asteroid asteroid-4" />
    </div>
  );
}
